import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Pills (Antonin, 13 Oct): the calendar's own selector keeps the pill look; only the page header's tabs are underlined. */
const pillClass = (on: boolean) => cn(
  'inline-flex h-8 shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-[8px] px-3 text-[length:var(--type-body-size)] font-medium leading-[var(--type-body-line)] transition-colors',
  on ? 'bg-[var(--color-blue-100)] text-[var(--color-blue-700)]' : 'text-text-secondary hover:bg-[var(--color-grey-100)] hover:text-text-primary',
);
import type { CalendarActor } from '@/scheduling/lib/calendar-actor';

/**
 * CalendarActorTabs — Departments | Devices (PROD-2007).
 *
 * The first tab's VISIBLE label is "Departments" (Ahmed, 28 Oct 2026); its id, the `?actor=` URL value and
 * the filter panel behind it stay `practitioners`. Label only: it still selects and filters practitioners.
 *
 * Ruling (Andrew, 12 Sep 2026): the calendar page has two tabs and the
 * switch REPLACES the actor set — practitioners and devices are never in the
 * same grid and there is no "both". Practitioners is today's calendar.
 *
 * Presentational: value in, change out. The page owns the URL (`?actor=`).
 */
interface CalendarActorTabsProps {
  value: CalendarActor;
  onChange: (actor: CalendarActor) => void;
  /**
   * Bookable-device count shown as a muted counter on the Devices tab.
   *
   * OMIT IT (or pass `undefined`) WHENEVER THE COUNT IS NOT KNOWN — PROD-2364.
   * The badge's whole job is telling an operator whether the other tab is
   * worth opening, so a placeholder `0` is not a harmless default: it is an
   * assertion that the practice has no devices. No badge asks the question;
   * `0` answers it wrongly. `0` is reserved for a practice that really has
   * none.
   */
  deviceCount?: number;
  /**
   * The practitioner filters live behind the Practitioners button (Antonin, 13 Oct: "integrate this filter system
   * within the practitioner button"): when given, the active Practitioners button names the selection and opens the
   * filter panel (specialties, the practitioners themselves).
   */
  practitionersMenu?: { open: boolean; onToggle: () => void; summary?: string; filtered?: boolean };
}

const TABS: ReadonlyArray<{ id: CalendarActor; label: string }> = [
  { id: 'practitioners', label: 'Departments' },
  { id: 'devices', label: 'Devices' },
];

export function CalendarActorTabs({ value, onChange, deviceCount, practitionersMenu }: CalendarActorTabsProps) {
  return (
    <div role="tablist" aria-label="Calendar actor" className="flex items-center gap-1">
      {TABS.map((tab) => {
        const active = tab.id === value;
        const count = tab.id === 'devices' ? deviceCount : undefined;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            {...(tab.id === 'practitioners' && practitionersMenu && active ? { 'aria-haspopup': 'dialog' as const, 'aria-expanded': practitionersMenu.open, 'data-practitioners-menu': '' } : {})}
            onClick={() => {
              if (!active) onChange(tab.id);
              else if (tab.id === 'practitioners' && practitionersMenu) practitionersMenu.onToggle();
            }}
            className={pillClass(active)}
          >
            {tab.label}
            {tab.id === 'practitioners' && practitionersMenu && active ? (
              <>
                {practitionersMenu.summary ? <span className="font-normal tabular-nums text-[var(--color-blue-700)]">{practitionersMenu.summary}</span> : null}
                {/* Filtered (fewer practitioners, or a specialty picked): a brand dot says the calendar is narrowed
                    (Antonin, 13 Oct: "a mark when it's not all and it's filtered"). */}
                {practitionersMenu.filtered ? <span data-filtered-mark aria-label="Filtered" className="h-2 w-2 rounded-full bg-[var(--color-brand-primary)]" /> : null}
                <ChevronDown aria-hidden className={cn('h-4 w-4 transition-transform', practitionersMenu.open && 'rotate-180')} />
              </>
            ) : null}
            {count != null ? (
              <span className={cn('font-normal tabular-nums', active ? 'text-[var(--color-blue-700)]' : 'text-text-secondary')}>{count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
